import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import ExpertSection from "@/components/ExpertSection";
import SEOHelmet from "@/components/SEOHelmet";
import StructuredData from "@/components/StructuredData";
import { 
  getTariffComparisonTableSchema,
  getTarifOverviewSchema,
  getTarifOverviewWebPageSchema
} from "@/utils/seoSchemas";

import HeroSection from "@/components/layout/HeroSection";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InteractiveCard } from "@/components/ui/interactive-card";
import { Shield, Star, TrendingUp, Award, Phone, ClipboardCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import tarifHero from "@/assets/tarifwissen-data-hero.jpg";
import { useImagePreload } from "@/hooks/useImagePreload";
const insuranceProviders = [{
  name: "Allianz",
  slug: "allianz",
  rating: "Sehr gut",
  highlight: "Marktführer mit umfassendem Schutz",
  category: "Premium"
}, {
  name: "Hiscox",
  slug: "hiscox",
  rating: "Ausgezeichnet",
  highlight: "Spezialist für Cyberversicherungen",
  category: "Premium"
}, {
  name: "HDI",
  slug: "hdi",
  rating: "Sehr gut",
  highlight: "24/7 Cyberschaden-Hotline",
  category: "Premium"
}, {
  name: "Cogitanda",
  slug: "cogitanda",
  rating: "Testsieger",
  highlight: "360° Cyberprotection",
  category: "Spezialist"
}, {
  name: "AXA",
  slug: "axa",
  rating: "Gut",
  highlight: "Flexible Lösungen für KMU",
  category: "Standard"
}, {
  name: "ERGO",
  slug: "ergo",
  rating: "Gut",
  highlight: "Starker Rundumschutz",
  category: "Standard"
}, {
  name: "Markel",
  slug: "markel",
  rating: "Sehr gut",
  highlight: "Internationale Expertise",
  category: "Premium"
}, {
  name: "Zurich",
  slug: "zurich",
  rating: "Gut",
  highlight: "Ab 137 EUR Jahresprämie",
  category: "Standard"
}, {
  name: "VHV",
  slug: "vhv",
  rating: "Ausgezeichnet",
  highlight: "CYBERPROTECT 3.0",
  category: "Premium"
}, {
  name: "Gothaer",
  slug: "gothaer",
  rating: "Gut",
  highlight: "24-Stunden-Cyber-Soforthilfe-Hotline",
  category: "Standard"
}, {
  name: "Alte Leipziger",
  slug: "alte-leipziger",
  rating: "Sehr gut",
  highlight: "Starke Leistungen im Test",
  category: "Premium"
}, {
  name: "R+V",
  slug: "ruv",
  rating: "Gut",
  highlight: "CyberRisk Versicherung",
  category: "Standard"
}, {
  name: "Signal Iduna",
  slug: "signal-iduna",
  rating: "Gut",
  highlight: "Schutz für Privat und Gewerbe",
  category: "Standard"
}, {
  name: "Württembergische",
  slug: "wuerttembergische",
  rating: "Gut",
  highlight: "Individuelles Baustein-System",
  category: "Standard"
}, {
  name: "Nürnberger",
  slug: "nuernberger",
  rating: "Gut",
  highlight: "24-Stunden-Hotline & Online-Plattform",
  category: "Standard"
}, {
  name: "Stoïk",
  slug: "stoik",
  rating: "Ausgezeichnet",
  highlight: "Mit aktivem Risikomanagement",
  category: "Spezialist"
}];
const Tarifwissen = () => {
  useImagePreload(tarifHero);
  const premiumProviders = insuranceProviders.filter(p => p.category === "Premium");
  const specialistProviders = insuranceProviders.filter(p => p.category === "Spezialist");
  const standardProviders = insuranceProviders.filter(p => p.category === "Standard");
  
  // Generate schemas
  const comparisonSchema = getTariffComparisonTableSchema(insuranceProviders);
  const itemListSchema = getTarifOverviewSchema(insuranceProviders);
  const webPageSchema = getTarifOverviewWebPageSchema();
  
  return (
    <>
      <SEOHelmet />
      <StructuredData data={[comparisonSchema, itemListSchema, webPageSchema]} />
      <div className="min-h-screen relative">
      
      <div className="relative z-10">
        <Navigation />
        <PageBreadcrumbs items={[
          { label: "Home", path: "/" },
          { label: "Tarifwissen" }
        ]} />
        
        {/* Hero Section */}
        <HeroSection
          image={tarifHero}
          alt="Cyberversicherung Tarifvergleich für Arztpraxen - Leistungsübersicht der führenden Versicherer für medizinische Einrichtungen"
          title="Cyberversicherungs-Tarife im Detail – Finden Sie den optimalen Schutz für Ihre Praxis"
          gradientPreset="none"
          imageOpacity={30}
          overlayColor="background"
          className="py-20 sm:py-24 lg:py-32 px-4"
        >
          <div className="container mx-auto max-w-4xl text-center relative z-10 animate-fade-in">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-accent/10 rounded-full mb-6">
              <ClipboardCheck className="w-7 h-7 text-accent" />
            </div>
            
            <h1 className="heading-1 mb-6 text-foreground leading-tight">Cyberversicherung
Anbieter-Vergleich</h1>
            
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-accent/5 rounded-full text-accent text-sm font-medium">
                Unabhängig • Transparent • Komplett
              </span>
            </div>
            
            <p className="body-text text-foreground/70 max-w-2xl mx-auto">
              Die besten Cyberversicherungen für Arztpraxen im direkten Vergleich
            </p>
          </div>
        </HeroSection>


      {/* Premium Providers */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <Award className="icon-sm text-accent" />
              <h2 className="heading-2">Premium Anbieter</h2>
            </div>
            <p className="body-text text-muted-foreground">
              Versicherer mit besonders umfangreichen Leistungen und Spezial-Know-how
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-responsive-md">
            {premiumProviders.map(provider => <InteractiveCard 
                key={provider.slug}
                className="border-border/50 hover:border-accent/50"
                enableTilt={true}
                enablePulse={true}
                enableDynamicShadow={true}
              >
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-xl sm:text-2xl">{provider.name}</CardTitle>
                    <Badge className="text-xs sm:text-sm bg-accent/10 text-accent border-accent/20 whitespace-nowrap pointer-events-none">{provider.rating}</Badge>
                  </div>
                  <CardDescription className="text-sm sm:text-base">
                    {provider.highlight}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to={`/tarifwissen/${provider.slug}`}>
                    <Button 
                      variant="outline" 
                      className="w-full text-sm sm:text-base group-hover:bg-accent/5 group-hover:border-accent/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] hover:shadow-[0_0_15px_hsl(var(--accent)/0.2)]"
                    >
                      Tarif ansehen
                    </Button>
                  </Link>
                </CardContent>
              </InteractiveCard>)}
          </div>
        </div>
      </section>

      {/* Specialist Providers */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <Star className="icon-sm text-accent" />
              <h2 className="heading-2">Cyber-Spezialisten</h2>
            </div>
            <p className="body-text text-muted-foreground">
              Anbieter mit spezialisiertem Fokus auf Cyberversicherungen
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-responsive-md">
            {specialistProviders.map(provider => <InteractiveCard 
                key={provider.slug}
                className="border-border/50 hover:border-accent/50"
                enableTilt={true}
                enablePulse={true}
                enableDynamicShadow={true}
              >
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-2xl">{provider.name}</CardTitle>
                    <Badge className="text-xs sm:text-sm bg-accent/10 text-accent border-accent/20 whitespace-nowrap pointer-events-none">{provider.rating}</Badge>
                  </div>
                  <CardDescription className="text-base">
                    {provider.highlight}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to={`/tarifwissen/${provider.slug}`}>
                    <Button 
                      variant="outline" 
                      className="w-full text-sm sm:text-base group-hover:bg-accent/5 group-hover:border-accent/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] hover:shadow-[0_0_15px_hsl(var(--accent)/0.2)]"
                    >
                      Tarif ansehen
                    </Button>
                  </Link>
                </CardContent>
              </InteractiveCard>)}
          </div>
        </div>
      </section>

      {/* Standard Providers */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <Shield className="icon-sm text-accent" />
              <h2 className="heading-2">Weitere Anbieter</h2>
            </div>
            <p className="body-text text-muted-foreground">
              Etablierte Versicherer mit soliden Cyberversicherungs-Angeboten
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-responsive-md">
            {standardProviders.map(provider => <InteractiveCard 
                key={provider.slug}
                className="border-border/50 hover:border-accent/50"
                enableTilt={true}
                enablePulse={true}
                enableDynamicShadow={true}
              >
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-2xl">{provider.name}</CardTitle>
                    <Badge className="text-xs sm:text-sm bg-accent/10 text-accent border-accent/20 whitespace-nowrap pointer-events-none">{provider.rating}</Badge>
                  </div>
                  <CardDescription className="text-base">
                    {provider.highlight}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to={`/tarifwissen/${provider.slug}`}>
                    <Button 
                      variant="outline" 
                      className="w-full text-sm sm:text-base group-hover:bg-accent/5 group-hover:border-accent/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] hover:shadow-[0_0_15px_hsl(var(--accent)/0.2)]"
                    >
                      Tarif ansehen
                    </Button>
                  </Link>
                </CardContent>
              </InteractiveCard>)}
          </div>
        </div>
      </section>

      {/* Related Links Section */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card/30 backdrop-blur-sm rounded-xl p-8 border border-border/50">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-center text-foreground">Weitere Informationen</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/cyberrisiken" className="group p-6 rounded-lg bg-card/50 border border-border/30 hover:border-accent/50 transition-all hover:shadow-lg">
                <AlertTriangle className="w-8 h-8 text-accent mb-3" />
                <h3 className="font-semibold text-foreground mb-2 group-hover:text-accent transition-colors">Cyberrisiken</h3>
                <p className="text-sm text-muted-foreground">Alle Bedrohungen im Überblick</p>
              </Link>
              <Link to="/cyberversicherung/vergleichsrechner" className="group p-6 rounded-lg bg-card/50 border border-border/30 hover:border-accent/50 transition-all hover:shadow-lg">
                <ClipboardCheck className="w-8 h-8 text-accent mb-3" />
                <h3 className="font-semibold text-foreground mb-2 group-hover:text-accent transition-colors">Vergleichsrechner</h3>
                <p className="text-sm text-muted-foreground">Tarife online vergleichen</p>
              </Link>
              <Link to="/weitere-versicherungen" className="group p-6 rounded-lg bg-card/50 border border-border/30 hover:border-accent/50 transition-all hover:shadow-lg">
                <Shield className="w-8 h-8 text-accent mb-3" />
                <h3 className="font-semibold text-foreground mb-2 group-hover:text-accent transition-colors">Weitere Versicherungen</h3>
                <p className="text-sm text-muted-foreground">Berufshaftpflicht, Inhaltsversicherung & mehr</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ExpertSection />
      <CTASection title="Unsicher, welcher Tarif zu Ihnen passt?" subtitle="Lassen Sie sich kostenlos und unverbindlich beraten" />
      
      <Footer />
      </div>
    </div>
    </>
  );
};
export default Tarifwissen;